import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { ProtectedRoute, PublicOnlyRoute, PermissionRoute } from './ProtectedRoute';
import { PublicLayout } from '../layouts/PublicLayout';
import { YimTalkLayout } from '../layouts/YimTalkLayout';
import { YimMeetLayout } from '../layouts/YimMeetLayout';
import { AdminLayout } from '../layouts/AdminLayout';
import { RouteErrorBoundary } from '../components/ErrorBoundary';

// ============================================
// LOADING COMPONENT
// ============================================

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-500 text-sm">Loading...</p>
      </div>
    </div>
  );
}

// ============================================
// LAZY-LOADED PAGES
// Enables code splitting for better performance
// ============================================

// Public Pages
const HomePage = lazy(() => import('../pages/public/HomePage'));
const AboutPage = lazy(() => import('../pages/public/AboutPage'));
const OurHistoryPage = lazy(() => import('../pages/public/OurHistoryPage'));
const ActivitiesPage = lazy(() => import('../pages/public/ActivitiesPage'));
const BlogPage = lazy(() => import('../pages/public/BlogPage'));
const BlogPostPage = lazy(() => import('../pages/public/BlogPostPage'));
const EventsPage = lazy(() => import('../pages/public/EventsPage'));
const EventDetailPage = lazy(() => import('../pages/public/EventDetailPage'));
const ContactPage = lazy(() => import('../pages/public/ContactPage'));
const GetInvolvedPage = lazy(() => import('../pages/public/GetInvolvedPage'));
const DonatePage = lazy(() => import('../pages/public/DonatePage'));
const PrivacyPolicyPage = lazy(() => import('../pages/public/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('../pages/public/TermsOfServicePage'));
const MyStatusPage = lazy(() => import('../pages/public/MyStatusPage'));
const GalleryPage = lazy(() => import('../pages/public/GalleryPage'));
const UnsubscribePage = lazy(() => import('../pages/public/UnsubscribePage'));
const YimTalkPage = lazy(() => import('../pages/public/yim-talk/YimTalkPage'));
const YimTalkAnswerPage = lazy(() => import('../pages/public/yim-talk/YimTalkAnswerPage'));
const YimTalkInboxPage = lazy(() => import('../pages/public/yim-talk/YimTalkInboxPage'));
const YimMeetJoinPage = lazy(() => import('../pages/public/yim-meet/YimMeetJoinPage'));
const YimFormPublicPage = lazy(() => import('../pages/public/yim-form/YimFormPublicPage'));

// Auth Pages
const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const AccountBlockedPage = lazy(() => import('../pages/auth/AccountBlockedPage'));
const ForgotPasswordPage = lazy(() => import('../pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('../pages/auth/ResetPasswordPage'));

// Admin Pages
const DashboardPage = lazy(() => import('../pages/admin/DashboardPage'));
const MembersPage = lazy(() => import('../pages/admin/MembersPage'));
const ActivitiesAdminPage = lazy(() => import('../pages/admin/ActivitiesPage'));
const BlogAdminPage = lazy(() => import('../pages/admin/BlogPage'));
const PrinciplesPage = lazy(() => import('../pages/admin/PrinciplesPage'));
const DonationsPage = lazy(() => import('../pages/admin/DonationsPage'));
const ContactsPage = lazy(() => import('../pages/admin/ContactsPage'));
const AuditLogsPage = lazy(() => import('../pages/admin/AuditLogsPage'));
const SettingsPage = lazy(() => import('../pages/admin/SettingsPage'));
const SchoolsPage = lazy(() => import('../pages/admin/SchoolsPage'));
const TeamPage = lazy(() => import('../pages/admin/TeamPage'));
const WebsitePage = lazy(() => import('../pages/admin/WebsitePage'));
const EventsAdminPage = lazy(() => import('../pages/admin/EventsPage'));
const BookingsAdminPage = lazy(() => import('../pages/admin/BookingsPage'));
const RegistrationsAdminPage = lazy(() => import('../pages/admin/RegistrationsPage'));
const ScanPage = lazy(() => import('../pages/admin/ScanPage'));
const GalleryAdminPage = lazy(() => import('../pages/admin/GalleryPage'));
const YimTalkAdminPage = lazy(() => import('../pages/admin/YimTalkAdminPage'));
const YimMeetAdminPage = lazy(() => import('../pages/admin/YimMeetAdminPage'));
const YimFormAdminPage = lazy(() => import('../pages/admin/YimFormAdminPage'));
const YimFormBuilderPage = lazy(() => import('../pages/admin/YimFormBuilderPage'));
const YimFormSubmissionsPage = lazy(() => import('../pages/admin/YimFormSubmissionsPage'));
const NotificationsPage = lazy(() => import('../pages/admin/NotificationsPage'));
const ServerLogsPage = lazy(() => import('../pages/admin/ServerLogsPage'));

// Error Pages
const NotFoundPage = lazy(() => import('../pages/errors/NotFoundPage'));
const UnauthorizedPage = lazy(() => import('../pages/errors/UnauthorizedPage'));

// ============================================
// SUSPENSE WRAPPER
// ============================================

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

// ============================================
// ROUTER CONFIGURATION
// ============================================

const router = createBrowserRouter([
  // ========================================
  // PUBLIC ROUTES
  // ========================================
  {
    path: '/',
    element: <PublicLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <HomePage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'about',
        element: (
          <SuspenseWrapper>
            <AboutPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'history',
        element: (
          <SuspenseWrapper>
            <OurHistoryPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'activities',
        element: (
          <SuspenseWrapper>
            <ActivitiesPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'blog',
        element: (
          <SuspenseWrapper>
            <BlogPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'blog/:slug',
        element: (
          <SuspenseWrapper>
            <BlogPostPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'events',
        element: (
          <SuspenseWrapper>
            <EventsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'event/:slug',
        element: (
          <SuspenseWrapper>
            <EventDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'contact',
        element: (
          <SuspenseWrapper>
            <ContactPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'get-involved',
        element: (
          <SuspenseWrapper>
            <GetInvolvedPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'donate',
        element: (
          <SuspenseWrapper>
            <DonatePage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'privacy',
        element: (
          <SuspenseWrapper>
            <PrivacyPolicyPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'terms',
        element: (
          <SuspenseWrapper>
            <TermsOfServicePage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'my-status',
        element: (
          <SuspenseWrapper>
            <MyStatusPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'gallery',
        element: (
          <SuspenseWrapper>
            <GalleryPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'unsubscribe/:token',
        element: (
          <SuspenseWrapper>
            <UnsubscribePage />
          </SuspenseWrapper>
        ),
      },
    ],
  },

  // ========================================
  // YIM TALK (immersive, no site header/footer)
  // ========================================
  {
    path: '/yim-talk',
    element: (
      <SuspenseWrapper>
        <YimTalkLayout />
      </SuspenseWrapper>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <YimTalkPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'answer/:id',
        element: (
          <SuspenseWrapper>
            <YimTalkAnswerPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'inbox/:id',
        element: (
          <SuspenseWrapper>
            <YimTalkInboxPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },

  // ========================================
  // YIM MEET (video, Google sign-in required)
  // ========================================
  {
    path: '/yim-meet',
    element: (
      <SuspenseWrapper>
        <YimMeetLayout />
      </SuspenseWrapper>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: 'join/:id',
        element: (
          <SuspenseWrapper>
            <YimMeetJoinPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },

  // ========================================
  // YIM FORM PUBLIC ROUTE
  // ========================================
  {
    path: '/yim-form/:slug',
    element: (
      <SuspenseWrapper>
        <YimFormPublicPage />
      </SuspenseWrapper>
    ),
    errorElement: <RouteErrorBoundary />,
  },

  // ========================================
  // AUTH ROUTES (Public only when logged out)
  // ========================================
  {
    path: '/login',
    element: (
      <PublicOnlyRoute>
        <SuspenseWrapper>
          <LoginPage />
        </SuspenseWrapper>
      </PublicOnlyRoute>
    ),
  },
  {
    path: '/account-blocked',
    element: (
      <SuspenseWrapper>
        <AccountBlockedPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/auth/login',
    element: (
      <PublicOnlyRoute>
        <SuspenseWrapper>
          <LoginPage />
        </SuspenseWrapper>
      </PublicOnlyRoute>
    ),
  },
  {
    path: '/auth/forgot-password',
    element: (
      <PublicOnlyRoute>
        <SuspenseWrapper>
          <ForgotPasswordPage />
        </SuspenseWrapper>
      </PublicOnlyRoute>
    ),
  },
  {
    path: '/auth/reset-password',
    element: (
      <SuspenseWrapper>
        <ResetPasswordPage />
      </SuspenseWrapper>
    ),
  },

  // ========================================
  // ADMIN ROUTES (Protected)
  // ========================================
  {
    path: '/admin',
    element: (
      <ProtectedRoute requiredRoles={['ADMIN']}>
        <AdminLayout />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <DashboardPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'dashboard',
        element: <Navigate to="/admin" replace />,
      },
      {
        path: 'members',
        element: (
          <PermissionRoute module="members">
            <SuspenseWrapper>
              <MembersPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'schools',
        element: (
          <PermissionRoute module="schools">
            <SuspenseWrapper>
              <SchoolsPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'activities',
        element: (
          <SuspenseWrapper>
            <ActivitiesAdminPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'blog',
        element: (
          <PermissionRoute module="blog">
            <SuspenseWrapper>
              <BlogAdminPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'events',
        element: (
          <PermissionRoute module="events">
            <SuspenseWrapper>
              <EventsAdminPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'bookings',
        element: (
          <PermissionRoute module="bookings">
            <SuspenseWrapper>
              <BookingsAdminPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'registrations',
        element: (
          <PermissionRoute module="events">
            <SuspenseWrapper>
              <RegistrationsAdminPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'scan',
        element: (
          <PermissionRoute module="bookings">
            <SuspenseWrapper>
              <ScanPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'principles',
        element: (
          <SuspenseWrapper>
            <PrinciplesPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'donations',
        element: (
          <PermissionRoute module="donations">
            <SuspenseWrapper>
              <DonationsPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'contacts',
        element: (
          <PermissionRoute module="contacts">
            <SuspenseWrapper>
              <ContactsPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'yim-talk',
        element: (
          <PermissionRoute module="yimtalk">
            <SuspenseWrapper>
              <YimTalkAdminPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'yim-meet',
        element: (
          <PermissionRoute module="yimmeet">
            <SuspenseWrapper>
              <YimMeetAdminPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'yim-form',
        element: (
          <PermissionRoute module="forms">
            <SuspenseWrapper>
              <YimFormAdminPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'yim-form/new',
        element: (
          <PermissionRoute module="forms">
            <SuspenseWrapper>
              <YimFormBuilderPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'yim-form/:id/edit',
        element: (
          <PermissionRoute module="forms">
            <SuspenseWrapper>
              <YimFormBuilderPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'yim-form/:id/submissions',
        element: (
          <PermissionRoute module="forms">
            <SuspenseWrapper>
              <YimFormSubmissionsPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'audit',
        element: (
          <PermissionRoute module="audit">
            <SuspenseWrapper>
              <AuditLogsPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'settings',
        element: (
          <SuspenseWrapper>
            <SettingsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'team',
        element: (
          <PermissionRoute module="users">
            <SuspenseWrapper>
              <TeamPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'website',
        element: (
          <PermissionRoute module="settings">
            <SuspenseWrapper>
              <WebsitePage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'gallery',
        element: (
          <PermissionRoute module="gallery">
            <SuspenseWrapper>
              <GalleryAdminPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'notifications',
        element: (
          <SuspenseWrapper>
            <NotificationsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'server-logs',
        element: (
          <SuspenseWrapper>
            <ServerLogsPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },

  // ========================================
  // ERROR ROUTES
  // ========================================
  {
    path: '/unauthorized',
    element: (
      <SuspenseWrapper>
        <UnauthorizedPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/404',
    element: (
      <SuspenseWrapper>
        <NotFoundPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '*',
    element: <Navigate to="/404" replace />,
  },
]);

// ============================================
// APP ROUTER COMPONENT
// ============================================

export function AppRouter() {
  return <RouterProvider router={router} />;
}

export default AppRouter;

